// @i18n-ignore-file
// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable i18next/no-literal-string */
// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */

import { DEFAULT_LOCALE } from '@src/config/localisation/locale-config.constants'
import type { TAbsoluteLocale, TAllLocale, TRegionDetail } from '@src/config/localisation/locale-config.types'
import { PageDataResponse } from '@src/server-side/api/api.types'
import { GatsbyImage } from '@src/types/image-data.type'
import { parseLocale } from '@src/utils/i18n.utils'

import { ELIGIBLE_LOCALES, TITLE_SUFFIX } from './constants'

import type { FaqSeoItem } from '@lastbrand/storefront-proto-registry/api/v1/seo'

export interface GetSeoMetapageDataProps {
  slug?: string
  imageUrl?: string
  metaImage?: GatsbyImage
  seoTitle?: string
  seoDescription?: string
  noindex?: boolean
  nofollow?: boolean
  title?: string
  description?: string
  canonicalUrl?: string
  /**
   * BFF-authored FAQ pairs surfaced for CLP rich-result schema. Carried
   * through the transform chain so the PL CLP schema builder can read them
   * off `pageMeta.seoData` regardless of which page entry path was taken.
   */
  faqData?: FaqSeoItem[]
  /** BFF-authored related-collection links for the CLP schema's significantLink array. */
  significantLink?: string[]
}

export interface SeoMetadata {
  title: string | null
  description: string | null
  robots: string
  alternates: {
    canonical: string
    languages: {
      'en-us': string
    }
  }
  openGraph: {
    title: string | null
    description: string | null
    images: { url: string }[] | null
    url: string
  }
  /** Forwarded as-is from input. The transformer doesn't reshape these — they're consumed by the CLP JSON-LD schema. */
  faqData?: FaqSeoItem[]
  significantLink?: string[]
}

export const getDefaultLanguageAdjustedLocale = (
  locale: TAbsoluteLocale,
  regionDetails?: TRegionDetail,
): TAllLocale => {
  const { country } = parseLocale(locale)
  const defaultLanguageIsoCodeForCountry = regionDetails?.regions[country]?.defaultLanguageIsoCode

  if (defaultLanguageIsoCodeForCountry === locale) {
    return country
  }
  return locale
}

const getCanonicalPageUrlWithLocale = (
  isI18nPreviewEnabled: boolean,
  locale?: TAbsoluteLocale,
  slugWithoutLeadingSlash?: string,
  regionDetails?: TRegionDetail,
) => {
  // Return same as pageUrl if i18n is disabled or if locale is the default locale
  const isDefaultLocale = locale === DEFAULT_LOCALE
  if (!locale || isDefaultLocale || !isI18nPreviewEnabled) {
    return `https://www.quince.com/${slugWithoutLeadingSlash}`
  }
  const defaultLanguageAdjustedLocale = getDefaultLanguageAdjustedLocale(locale, regionDetails)
  // Prefer the region's URL display code (e.g. gb -> uk) so canonical/og match the actual
  // /uk routing prefix (ULU-197). Only swap in the default-language case, where the adjusted
  // value equals the country code; locale-specific prefixes like fr-ca must stay untouched.
  const { country } = parseLocale(locale)
  const displayCode = regionDetails?.regions[country]?.regionDisplayCode
  const urlPrefix =
    defaultLanguageAdjustedLocale === country && displayCode ? displayCode : defaultLanguageAdjustedLocale
  return `https://www.quince.com/${urlPrefix}/${slugWithoutLeadingSlash}`
}

const HOMEPAGE_BASE_URL = 'https://www.quince.com'

export const getHomepageUrlForLocale = (
  locale?: TAbsoluteLocale,
  regionDetails?: TRegionDetail,
  isI18nPreviewEnabled = true,
): string => {
  if (!locale || locale === DEFAULT_LOCALE || !isI18nPreviewEnabled) {
    return `${HOMEPAGE_BASE_URL}/`
  }
  const defaultLanguageAdjustedLocale = getDefaultLanguageAdjustedLocale(locale, regionDetails)
  const { country } = parseLocale(locale)
  const displayCode = regionDetails?.regions[country]?.regionDisplayCode
  const urlPrefix =
    defaultLanguageAdjustedLocale === country && displayCode ? displayCode : defaultLanguageAdjustedLocale
  return `${HOMEPAGE_BASE_URL}/${urlPrefix}`
}

export const buildHomepageAlternateUrls = (
  regionDetails?: TRegionDetail,
  isI18nPreviewEnabled = true,
): Record<string, string> => {
  const urls: Record<string, string> = {}
  if (!regionDetails || !isI18nPreviewEnabled) return urls
  Object.values(regionDetails.regions).forEach(region => {
    region?.supportedLanguagesIsoCodes.forEach(supportedLocale => {
      urls[supportedLocale.toLowerCase()] = getHomepageUrlForLocale(
        supportedLocale,
        regionDetails,
        isI18nPreviewEnabled,
      )
    })
  })
  return urls
}

export const appendSEOTitleSuffix = (
  title: string | null,
  locale: TAbsoluteLocale | undefined,
  regionDetails?: TRegionDetail,
): string | null => {
  if (!title || !locale) return title

  const { country } = parseLocale(locale)
  if (!ELIGIBLE_LOCALES.includes(locale)) return title

  const trimmedTitle = title.trimEnd()
  const countryName = regionDetails?.regions[country]?.displayName
  const suffix = `${TITLE_SUFFIX} ${countryName}`

  if (trimmedTitle.endsWith(suffix)) return trimmedTitle

  return `${trimmedTitle} ${suffix}`
}

export const trimSEOTitleSuffix = (title: string | null) => {
  if (!title) return title
  return title.replace(/\s*\|\s*Quince\s*$/i, '').trim()
}

/**
 * Appends a pre-built query suffix (e.g. `filters=colors_brown`) to a URL, picking `?` or
 * `&` based on whether the base already has a query string. Returns the base unchanged when
 * the suffix is empty. Single source of truth shared by the page-level canonical/og:url, the
 * JSON-LD url/@id, and the hreflang alternates so every filter signal stays byte-aligned.
 */
export function appendQuerySuffix(base: string, suffix: string | undefined): string {
  if (!suffix) return base
  const separator = base.includes('?') ? '&' : '?'
  return `${base}${separator}${suffix}`
}

export const getSeoMetapageData = (
  isI18nPreviewEnabled: boolean,
  seoData?: GetSeoMetapageDataProps | null | undefined,
  locale?: TAbsoluteLocale,
  regionDetails?: TRegionDetail,
  isAppendQuinceLocale = false,
  isHomepage = false,
  canonicalFilterSuffix?: string,
  isV2Format = false,
): SeoMetadata | null => {
  if (!seoData) return null
  const slugWithoutLeadingSlash = seoData.slug?.startsWith('/') ? seoData.slug.substring(1) : seoData.slug
  const pageUrl = `https://www.quince.com/${slugWithoutLeadingSlash}`
  const canonicalUrl = getCanonicalPageUrlWithLocale(
    isI18nPreviewEnabled,
    locale,
    slugWithoutLeadingSlash,
    regionDetails,
  )
  // og:url must agree with the canonical tag (incl. locale prefix and any
  // explicit override) so crawlers don't see conflicting canonicalization
  // signals between /ca, /fr-ca and the US path.
  let resolvedCanonicalUrl = seoData.canonicalUrl || canonicalUrl
  // Append applied v2 filters after the resolved base so it works whether the
  // canonical came from the API (`seoData.canonicalUrl`) or the slug fallback, and so
  // canonical + og:url stay in sync (both read `resolvedCanonicalUrl`).
  //
  // Append ONLY when v2 is the active format. `isV2Format` is the authoritative gate threaded
  // from getSelectedFilterFromQueryParamsWithV2Support; when it is false the canonical + og:url
  // are left untouched (prior behavior). appendQuerySuffix no-ops on an empty suffix.
  if (isV2Format) {
    resolvedCanonicalUrl = appendQuerySuffix(resolvedCanonicalUrl, canonicalFilterSuffix)
  }
  if (isHomepage) {
    resolvedCanonicalUrl = getHomepageUrlForLocale(locale, regionDetails, isI18nPreviewEnabled)
  }

  const index = seoData.noindex === true ? 'noindex' : 'index'
  const follow = seoData.nofollow === true ? 'nofollow' : 'follow'
  const seoTitle = trimSEOTitleSuffix(seoData.seoTitle || seoData.title || null)
  // Not append locale specific suffix for homepage and if the feature gate is disabled
  const title = !isHomepage && isAppendQuinceLocale ? appendSEOTitleSuffix(seoTitle, locale, regionDetails) : seoTitle
  const description = seoData.seoDescription || seoData.description || null
  const seoImage = seoData.imageUrl || seoData.metaImage?.file.url

  return {
    title,
    description,
    robots: `${index}, ${follow}`,
    alternates: {
      canonical: resolvedCanonicalUrl,
      languages: {
        [DEFAULT_LOCALE]: pageUrl,
      },
    },
    openGraph: {
      title,
      description,
      images: seoImage ? [{ url: seoImage }] : null,
      url: resolvedCanonicalUrl,
    },
    // Pass-through fields the transformer doesn't reshape — kept conditional so
    // the emitted object stays clean for pages that don't author them.
    ...(seoData.faqData?.length ? { faqData: seoData.faqData } : {}),
    ...(seoData.significantLink ? { significantLink: seoData.significantLink } : {}),
    ...(seoData.canonicalUrl ? { canonicalUrl: seoData.canonicalUrl } : {}),
  }
}

export function getSeoMetaDataForPage(
  isI18nPreviewEnabled: boolean,
  pageData?: PageDataResponse | null,
  locale?: TAbsoluteLocale,
  regionDetails?: TRegionDetail,
  isAppendQuinceLocale = false,
  isHomepage = false,
  canonicalFilterSuffix?: string,
  isV2Format = false,
): SeoMetadata | null {
  const seoData =
    pageData?.faqPage ||
    pageData?.landingPage ||
    pageData?.authenticationPage ||
    pageData?.accountPage ||
    pageData?.context?.seoData
  return getSeoMetapageData(
    isI18nPreviewEnabled,
    seoData,
    locale,
    regionDetails,
    isAppendQuinceLocale,
    isHomepage,
    canonicalFilterSuffix,
    isV2Format,
  )
}
